import { useMemo, useState } from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import { Button, Chip, Drawer, Icon, Stack, Text, useDefenceVariant, tokens } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';
import { LayerPreviewSwatch } from './LayerPreviewSwatch';
import {
  layerMatchesQuery,
  providerIcon,
  providerIdFromLayer,
  providerLabel,
} from './layerModel';
import { ProviderSourcePicker } from './ProviderSourcePicker';
import { PivotLink } from '@/components/pivots/PivotLink';

interface AddLayerDrawerProps {
  open: boolean;
  onClose(): void;
}

function providerReason(allowed: boolean): string | null {
  return allowed ? null : 'Unavailable in defence variant';
}

export function AddLayerDrawer({ open, onClose }: AddLayerDrawerProps) {
  const catalog = useViewerStore((state) => state.catalog);
  const visibleLayerIds = useViewerStore((state) => state.visibleLayerIds);
  const setVisible = useViewerStore((state) => state.setVisible);
  const setSelection = useViewerStore((state) => state.setSelection);
  const defence = useDefenceVariant();
  const [query, setQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [providerPickerOpen, setProviderPickerOpen] = useState(false);

  const hiddenCatalog = useMemo(
    () => catalog.filter((layer) => !visibleLayerIds.has(layer.id)),
    [catalog, visibleLayerIds],
  );

  const filteredLayers = useMemo(() => {
    return hiddenCatalog.filter((layer) => {
      if (!layerMatchesQuery(layer, query)) return false;
      if (providerFilter === 'all') return true;
      return providerIdFromLayer(layer) === providerFilter;
    });
  }, [hiddenCatalog, providerFilter, query]);

  const providerCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const layer of hiddenCatalog) {
      const provider = providerIdFromLayer(layer);
      counts.set(provider, (counts.get(provider) ?? 0) + 1);
    }
    return counts;
  }, [hiddenCatalog]);

  const selectableProviders = useMemo(() => {
    const ids = [...providerCounts.keys()].sort((left, right) => left.localeCompare(right));
    return ['all', ...ids];
  }, [providerCounts]);

  const isAllowed = (providerId: string) => defence.allowProvider(providerId);
  const addLayer = (layer: Layer) => {
    const providerId = providerIdFromLayer(layer);
    if (!isAllowed(providerId)) return;
    setVisible(layer.id, true);
    setSelection({ layerId: layer.id });
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <Drawer
        id="gvw-032-add-layer-drawer"
        title="Add layer"
        onClose={onClose}
        width={tokens.layout.rightPanel}
        nonModal
      >
        <Stack direction="column" gap={2}>
          <Text variant="body" as="p" style={{ margin: 0, color: tokens.color.ink.secondary }}>
            Browse the current catalog and add supported layers to the map.
          </Text>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: tokens.space[2],
            }}
          >
            <label style={{ display: 'grid', gap: tokens.space[1] }}>
              <Text variant="caption" as="span">
                Search catalog
              </Text>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by label, id, kind, provider"
                style={{
                  minHeight: tokens.density.touchMin,
                  borderRadius: tokens.radius.input,
                  border: tokens.border.thin,
                  background: tokens.color.surface[2],
                  color: tokens.color.ink.primary,
                  paddingInline: tokens.space[2],
                }}
              />
            </label>
            <Button variant="ghost" onClick={() => setProviderPickerOpen(true)}>
              <Icon name="filter_list" size={20} decorative />
              <span>Provider</span>
            </Button>
          </div>

          <Stack direction="row" gap={1} style={{ flexWrap: 'wrap' }}>
            {selectableProviders.map((providerId) => {
              const label =
                providerId === 'all'
                  ? `All (${hiddenCatalog.length})`
                  : `${providerLabel(providerId)} (${providerCounts.get(providerId) ?? 0})`;
              return (
                <Chip
                  key={providerId}
                  icon={providerId === 'all' ? 'layers' : providerIcon(providerId)}
                  label={label}
                  variant={providerFilter === providerId ? 'info' : 'neutral'}
                  pressable
                  pressed={providerFilter === providerId}
                  onClick={() => setProviderFilter(providerId)}
                  aria-label={`Filter by ${label}`}
                />
              );
            })}
          </Stack>

          {filteredLayers.length === 0 ? (
            <div
              style={{
                border: tokens.border.thin,
                borderRadius: tokens.radius.md,
                padding: tokens.space[3],
                background: tokens.color.surface[2],
              }}
            >
              <Stack direction="row" align="center" gap={2}>
                <Icon name="search" size={20} decorative />
                <Text variant="body" as="p" style={{ margin: 0 }}>
                  No catalog matches. Try another filter or query.
                </Text>
              </Stack>
            </div>
          ) : (
            <div role="list" aria-label="Catalog layer results">
              <Stack direction="column" gap={2}>
                {filteredLayers.map((layer) => {
                  const providerId = providerIdFromLayer(layer);
                  const allowed = isAllowed(providerId);
                  const reason = providerReason(allowed);
                  return (
                    <div
                      key={layer.id}
                      role="listitem"
                      style={{
                        border: tokens.border.thin,
                        borderRadius: tokens.radius.md,
                        padding: tokens.space[2],
                        background: tokens.color.surface[2],
                      }}
                    >
                      <Stack direction="row" align="center" gap={2}>
                        <LayerPreviewSwatch layer={layer} />
                        <Stack direction="column" gap={1} style={{ flex: '1 1 auto', minWidth: 0 }}>
                          <Text variant="body" as="span">
                            {layer.label ?? layer.id}
                          </Text>
                          <Stack direction="row" gap={1} style={{ flexWrap: 'wrap' }}>
                            <Chip icon="layers" label={layer.kind} variant="neutral" />
                            <Chip
                              icon={providerIcon(providerId)}
                              label={providerLabel(providerId)}
                              variant={allowed ? 'neutral' : 'warning'}
                            />
                            <Chip icon="language" label={layer.crs} variant="neutral" />
                            {!allowed && reason ? (
                              <Chip icon="block" label={reason} variant="error" />
                            ) : null}
                          </Stack>
                        </Stack>
                        <Button
                          variant="primary"
                          onClick={() => addLayer(layer)}
                          disabled={!allowed}
                          aria-label={`Add layer ${layer.label ?? layer.id}`}
                        >
                          <Icon name="add" size={20} decorative />
                          <span>Add</span>
                        </Button>
                      </Stack>
                    </div>
                  );
                })}
              </Stack>
            </div>
          )}

          <PivotLink
            pivotId="geoconverter-upload"
            context={{
              extra: {
                collectionId: 'default', // TODO: Get from current workspace/org context
              },
            }}
            onPivot={() => onClose()}
          >
            <Icon name="sync_alt" size={20} decorative />
            <span>Open in GeoConverter</span>
          </PivotLink>
        </Stack>
      </Drawer>
      <ProviderSourcePicker
        open={providerPickerOpen}
        selectedProviderId={providerFilter === 'all' ? null : providerFilter}
        isProviderAllowed={isAllowed}
        disabledReason={(providerId) => providerReason(isAllowed(providerId))}
        onSelect={(providerId) => {
          setProviderFilter(providerId);
          setProviderPickerOpen(false);
        }}
        onClose={() => setProviderPickerOpen(false)}
      />
    </>
  );
}
